import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { DueListRoutes, UserHaccpRoutes, UserTasksRoutes } from 'src/app/core/enums/routes.enum';
import { ClearLastDueListItem } from 'src/app/due-list/state/due-list.actions';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { HaccpLog } from '../models/haccp-log.model';
import { IHaccpPerformResponse } from '../models/haccp.model';
import { HaccpApiService } from '../services/haccp-api.service';
import {
  CreateHaccp,
  DeleteHaccp,
  LoadHaccpDraftPerform,
  LoadHaccpLog,
  LoadHaccpPerform,
  PerformHaccp,
  PostponeHaccp,
  PrintExcel,
  RestoreHaccp,
  SaveHaccpDraftPerform,
  UpdateHaccp,
  UpdateHaccpPerform,
} from './haccp.actions';

export interface HaccpStateModel {
  haccpLog: HaccpLog;
  draftPerform: IHaccpPerformResponse | undefined;
  perform: IHaccpPerformResponse | undefined;
  excelFile: Blob;
}

const HACCP_STATE_TOKEN = new StateToken<HaccpStateModel>('haccp');

@State({
  name: HACCP_STATE_TOKEN,
  defaults: {
    haccpLog: { columns: [], records: [] },
    draftPerform: undefined,
    perform: undefined,
    excelFile: <Blob>{},
  },
})
@Injectable()
export class HaccpState {
  constructor(
    private service: HaccpApiService,
    private store: Store
  ) {}

  @Action(LoadHaccpLog, { cancelUncompleted: true })
  loadHaccpLogs(
    { getState, setState }: StateContext<HaccpStateModel>,
    { haccpId, startDate, endDate, showAllColumns, sortByPerformedAscending }: LoadHaccpLog
  ) {
    return this.service
      .loadHaccpLog(haccpId, startDate, endDate, showAllColumns, sortByPerformedAscending)
      .pipe(
        tap((haccpLog) => {
          setState({ ...getState(), haccpLog });
        })
      );
  }

  @Action(CreateHaccp, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskCreated' })
  createHaccp(ctx: StateContext<HaccpStateModel>, { haccp }: CreateHaccp) {
    return this.service.createHaccp(haccp).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Overview}/${haccp.menuId}`);
      })
    );
  }

  @Action(UpdateHaccp, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskUpdated' })
  updateHaccp(ctx: StateContext<HaccpStateModel>, { haccp }: UpdateHaccp) {
    return this.service.updateHaccp(haccp).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserTasksRoutes.Overview}/${haccp.menuId}`);
      })
    );
  }

  @Action(DeleteHaccp, { cancelUncompleted: true })
  @OnSuccess({ url: UserHaccpRoutes.Main, message: 'extendedTaskDeleted' })
  deleteHaccp(ctx: StateContext<HaccpStateModel>, { haccpId }: DeleteHaccp) {
    return this.service.deleteHaccp(haccpId);
  }

  @Action(PostponeHaccp, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskPostponed' })
  postponeHaccp(ctx: StateContext<HaccpStateModel>, { haccpPostpone }: PostponeHaccp) {
    return this.service.postponeHaccp(haccpPostpone);
  }

  @Action(PerformHaccp, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskPerformed' })
  performHaccp(ctx: StateContext<HaccpStateModel>, { haccp }: PerformHaccp) {
    return this.service.performHaccp(haccp).pipe(
      tap(() => {
        const isFromDueList =
          this.store.selectSnapshot(DueListState.getLastDueListItem)?.id === haccp.id;
        if (!isFromDueList) {
          redirectAndBypassUnsavedDataCheck(`${UserHaccpRoutes.Edit}/${haccp.id}`, {
            queryParams: { activeTab: 'log' },
          });
        } else {
          this.store.dispatch(new ClearLastDueListItem());
          redirectAndBypassUnsavedDataCheck(DueListRoutes.Main);
        }
      })
    );
  }

  @Action(UpdateHaccpPerform, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskPerformed' })
  updateHaccpPerform(ctx: StateContext<HaccpStateModel>, { haccp, versionId }: UpdateHaccpPerform) {
    return this.service.updateHaccpPerform(haccp, versionId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserHaccpRoutes.Edit}/${haccp.id}`, {
          queryParams: { activeTab: 'log' },
        });
      })
    );
  }

  @Action(RestoreHaccp, { cancelUncompleted: true })
  @OnSuccess({ message: 'extendedTaskRestored', url: UserHaccpRoutes.Main })
  restoreHaccp(ctx: StateContext<HaccpStateModel>, { id }: RestoreHaccp) {
    return this.service.restoreHaccp(id);
  }

  @Action(LoadHaccpDraftPerform, { cancelUncompleted: true })
  loadHaccpDraftPerform(
    { getState, setState }: StateContext<HaccpStateModel>,
    { haccpId }: LoadHaccpDraftPerform
  ) {
    return this.service.loadHaccpDraftPerform(haccpId).pipe(
      tap((draftPerform) => {
        setState({ ...getState(), draftPerform });
      })
    );
  }

  @Action(LoadHaccpPerform, { cancelUncompleted: true })
  loadHaccpPerform(
    { getState, setState }: StateContext<HaccpStateModel>,
    { haccpId, haccpVersionId }: LoadHaccpPerform
  ) {
    return this.service.loadHaccpPerform(haccpId, haccpVersionId).pipe(
      tap((perform) => {
        setState({ ...getState(), perform });
      })
    );
  }

  @Action(SaveHaccpDraftPerform, { cancelUncompleted: true })
  @OnSuccess({ message: 'draftSaved' })
  saveHaccpDraftPerform(
    { getState, setState }: StateContext<HaccpStateModel>,
    { haccp }: SaveHaccpDraftPerform
  ) {
    return this.service.saveHaccpDraftPerform(haccp).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserHaccpRoutes.Edit}/${haccp.id}`);
      })
    );
  }

  @Action(PrintExcel, { cancelUncompleted: true })
  printExcel(
    { getState, setState }: StateContext<HaccpStateModel>,
    { id, from, to, showAllColumns }: PrintExcel
  ) {
    return this.service.printExcel(id, from, to, showAllColumns).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          excelFile: file,
        });
      })
    );
  }

  @Selector()
  static getHaccpLog(state: HaccpStateModel) {
    return state.haccpLog;
  }

  @Selector()
  static getDraftPerform(state: HaccpStateModel) {
    return state.draftPerform;
  }

  @Selector()
  static getPerform(state: HaccpStateModel) {
    return state.perform;
  }

  @Selector()
  static getHaccpAsExcel(state: any) {
    return state.excelFile;
  }
}
